import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Alert, Box, Divider, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DealerHeader from "./components/DealerHeader";
import DealerQuoteForm from "./components/DealerQuoteForm";
import DealerResults from "./components/DealerResults";
import {
  initialFormData,
  packageDefinitions,
  provinceNameToCodeMap,
} from "./constants";
import { RATE_DIVISOR, getStoredRateTable } from "./admin/ratesConfig";
import {
  getZoneDisplayName,
  resolveZone,
} from "../cvt/data/zoneMap";

function Dealer() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("preventivi");
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [quoteResult, setQuoteResult] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const handleFieldChange = (field) => (event) => {
    let value = event.target.value;

    if (field === "targa") {
      value = value.toUpperCase().replace(/\s+/g, "").slice(0, 10);
    }

    if (field === "valoreVeicolo") {
      value = value.replace(/[^\d]/g, "").slice(0, 8);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, submit: undefined }));
  };

  const handleDurationSelect = (duration) => {
    setFormData((prev) => ({ ...prev, durata: duration }));
    setErrors((prev) => ({ ...prev, durata: undefined, submit: undefined }));
  };

  const toggleSection = (packId) => {
    setOpenSections((prev) => ({
      ...prev,
      [packId]: !prev[packId],
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.marca.trim()) nextErrors.marca = "Inserisci la marca";
    if (!formData.modello.trim()) nextErrors.modello = "Inserisci il modello";
    if (!formData.provincia) nextErrors.provincia = "Seleziona una provincia";

    const valoreNumeric = Number(formData.valoreVeicolo);
    if (!formData.valoreVeicolo || Number.isNaN(valoreNumeric) || valoreNumeric <= 0) {
      nextErrors.valoreVeicolo = "Inserisci un valore veicolo valido";
    }

    if (!formData.durata) nextErrors.durata = "Seleziona una durata";

    if ((formData.nome && !formData.cognome) || (!formData.nome && formData.cognome)) {
      nextErrors.intestatario = "Compila sia nome che cognome oppure lascia entrambi vuoti";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const valore = Number(formData.valoreVeicolo);
    const provinceCode = provinceNameToCodeMap[formData.provincia];
    const zone = resolveZone(provinceCode);
    const table = getStoredRateTable();

    const packs = packageDefinitions.map((pack) => {
      const pkgKey = pack.ratePackageKey;
      const rate = Number(table?.[zone]?.[pkgKey]?.[formData.durata]);
      const effectiveRate = Number.isFinite(rate) ? rate : 0;
      const total = Math.round((valore * (effectiveRate / RATE_DIVISOR)) * 100) / 100;
      return {
        ...pack,
        rate: effectiveRate,
        total,
        monthly: total / formData.durata,
      };
    });

    setQuoteResult({
      zone: getZoneDisplayName(zone),
      packs,
      timestamp: new Date().toLocaleString("it-IT"),
    });
    setToastOpen(true);
  };

  return (
    <React.Fragment>
      <Helmet title="Dealer" />

      <DealerHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onOpenAdminRates={() => navigate("/pages/dealer/admin-rates")}
      />

      <Divider sx={{ mt: 4, mb: 4 }} />

      {activeTab === "preventivi" ? (
        <Box
          sx={{
            mt: 0,
            display: "grid",
            gap: { xs: 4, lg: 6 },
            gridTemplateColumns: { xs: "1fr", lg: "520px 1fr" },
            alignItems: "start",
          }}
        >
          <DealerQuoteForm
            formData={formData}
            errors={errors}
            handleFieldChange={handleFieldChange}
            handleDurationSelect={handleDurationSelect}
            handleSubmit={handleSubmit}
          />
          <DealerResults
            activeTab={activeTab}
            formData={formData}
            quoteResult={quoteResult}
            openSections={openSections}
            toggleSection={toggleSection}
            onBackToPreventivi={() => setActiveTab("preventivi")}
          />
        </Box>
      ) : (
        <DealerResults
          activeTab={activeTab}
          formData={formData}
          quoteResult={quoteResult}
          openSections={openSections}
          toggleSection={toggleSection}
          onBackToPreventivi={() => setActiveTab("preventivi")}
        />
      )}

      <Snackbar
        open={toastOpen}
        autoHideDuration={2800}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled">
          Preventivo calcolato - {quoteResult?.zone || "Zona 1"}, {quoteResult?.packs?.length || 0} pacchetti disponibili
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default Dealer;
